import sparePartsApi, { ApiResponse } from "../../../../api/spareParts.api";

import {
  Pagination,
  PurchaseBill,
  PurchaseBillApiRow,
  PurchaseBillFilters,
  PurchaseBillListResponse,
  PurchaseBillPayload,
  PurchaseBillStatus,
  SelectOption,
} from "../types";

const BASE_URL = "/procurement/purchase-bills";

const toNumber = (value: string | number | null | undefined) =>
  Number(value ?? 0);

const mapPurchaseBill = (bill: PurchaseBillApiRow): PurchaseBill => ({
  id: bill.id,
  code: bill.code,
  invoiceNumber: bill.invoiceNumber,
  supplierId: bill.supplierId,
  supplierName: bill.supplier?.supplierName || "-",
  invoiceDate: bill.invoiceDate,
  purchaseDate: bill.purchaseDate,
  currency: bill.currency,
  warehouseId: bill.warehouseId,
  subtotal: toNumber(bill.subtotal),
  otherCharges: toNumber(bill.otherCharges),
  totalAmount: toNumber(bill.totalAmount),
  totalItems: bill.items?.length || 0,
  remarks: bill.remarks,
  status: bill.status,
  items: (bill.items || []).map((item) => ({
    id: item.id,
    sparePartId: item.sparePartId,
    sparePartName: item.sparePart?.name || "-",
    partCode: item.sparePart?.code || item.sparePart?.partNumber || "-",
    quantity: item.quantity,
    receivedQuantity: item.receivedQuantity,
    unitPrice: toNumber(item.unitPrice),
    totalAmount: toNumber(item.totalPrice),
    remarks: item.remarks,
  })),
  createdAt: bill.createdAt,
  updatedAt: bill.updatedAt,
});

class PurchaseBillService {
  async getPurchaseBills(
    params?: PurchaseBillFilters
  ): Promise<PurchaseBillListResponse> {
    const response = await sparePartsApi.get<ApiResponse<PurchaseBillApiRow[]>>(
      BASE_URL,
      { params }
    );

    return {
      items: (response.data.data || []).map(mapPurchaseBill),
      pagination: response.data.meta as Pagination,
    };
  }

  async getPurchaseBillById(id: string): Promise<PurchaseBill> {
    const response = await sparePartsApi.get<ApiResponse<PurchaseBillApiRow>>(
      `${BASE_URL}/${id}`
    );

    return mapPurchaseBill(response.data.data);
  }

  async createPurchaseBill(payload: PurchaseBillPayload): Promise<PurchaseBill> {
    const response = await sparePartsApi.post<ApiResponse<PurchaseBillApiRow>>(
      BASE_URL,
      payload
    );

    return mapPurchaseBill(response.data.data);
  }

  async updatePurchaseBill(
    id: string,
    payload: PurchaseBillPayload
  ): Promise<PurchaseBill> {
    const response = await sparePartsApi.put<ApiResponse<PurchaseBillApiRow>>(
      `${BASE_URL}/${id}`,
      payload
    );

    return mapPurchaseBill(response.data.data);
  }

  async updateStatus(
    id: string,
    status: PurchaseBillStatus,
    warehouseId?: string | null
  ): Promise<void> {
    await sparePartsApi.patch(`${BASE_URL}/${id}/status`, {
      status,
      warehouseId: warehouseId || undefined,
    });
  }

  async deletePurchaseBill(id: string): Promise<void> {
    await sparePartsApi.delete(`${BASE_URL}/${id}`);
  }

  async getSupplierOptions(): Promise<SelectOption[]> {
    const response = await sparePartsApi.get<
      ApiResponse<Array<{ id: string; code: string; supplierName: string }>>
    >("/suppliers/dropdown");

    return (response.data.data || []).map((supplier) => ({
      id: supplier.id,
      name: supplier.code
        ? `${supplier.supplierName} (${supplier.code})`
        : supplier.supplierName,
      code: supplier.code,
    }));
  }

  async getSparePartOptions(): Promise<SelectOption[]> {
    const response = await sparePartsApi.get<ApiResponse<SelectOption[]>>(
      "/spare-parts/dropdown"
    );

    return response.data.data || [];
  }

  async getHubOptions(): Promise<SelectOption[]> {
    const response = await sparePartsApi.get<ApiResponse<SelectOption[]>>(
      "/integrations/hubs"
    );

    return (response.data.data || []).map((hub) => ({
      id: hub.id,
      name: hub.code ? `${hub.name} (${hub.code})` : hub.name,
      code: hub.code,
    }));
  }
}

export default new PurchaseBillService();
